import { normalizeItemNotas } from '../utils/formatters';
import api from './api';
import clienteService from './clienteService';
import productoService from './productoService';
import transporteService from './transporteService';
import movMonedaService from './movMonedaService';

export interface FacturaVentaDetalle {
    nroIdformulario: number;
    nroItem: number;
    nroFormularioMaster?: number;
    nroItemMaster?: number;
    codigoArticulo?: string;
    descripcionArticulo?: string;
    cantidad?: number;
    precioUnitario?: number;
    precioLista?: number;
    descuento?: number;
    porcDesc?: number;
    subtotal?: number;
    notas?: string;
    um?: string;
    codigoMoneda?: number;
    cotizacion?: number;
}

export interface FacturaVenta {
    nroFormulario: number;
    fechaEmision: string | Date;
    horaEmision?: string | Date;
    nroTipoFormulario?: string;
    tipoCliente?: string;
    tipoIva?: string;
    letra?: string;
    nroSuc?: number;
    nroComprobante?: number;
    nroCliente?: number;
    nombre?: string;
    nroVendedor?: number;
    nroLista?: string;
    codigoMoneda?: number | string;
    subtotal?: number;
    descuento?: number;
    importeSinIva?: number;
    exento?: number;
    ivainscripto?: number;
    ivanoInscripto?: number;
    ivaotro?: number;
    importeTotal?: number;
    asentado?: string;
    /** Número de asiento en libro diario cuando el comprobante está contabilizado. */
    nroAsiento?: number | null;
    descripcion?: string;
    usuario?: string;
    cuit?: string;
    caeNro?: string;
    caeVencimiento?: string | null;
    caeResultado?: string;
    caeObs?: string;
    detalles?: FacturaVentaDetalle[];
    porcDesc?: number | null;
    porcDesc2?: number | null;
    porcDesc3?: number | null;
    ordenCompra?: string;
    nroFormularioRemito?: number | null;
    transporte?: string;
    ubicacion?: string;
    formaDePago?: string;
    idCompania?: string;
}

export interface FacturaVentaSearchParams {
    searchTerm?: string;
    pageNumber: number;
    pageSize: number;
    fechaDesde?: Date;
    fechaHasta?: Date;
    nroCliente?: number;
    nroSuc?: number;
    nombreCliente?: string;
    /** Letra del comprobante (ej. A, B, C) - identificación del documento */
    letra?: string;
    /** Número de comprobante - identificación del documento */
    nroComprobante?: number;
    /** ConCae | SinCae | Interno — filtro de estado fiscal (opcional) */
    estadoFactura?: string;
}

export interface FacturaVentaPagedResult {
    items: FacturaVenta[];
    totalCount: number;
    pageNumber: number;
    pageSize: number;
    totalPages: number;
}

/** Quita relleno tipo CHAR(n) en notas de cada ítem al recibir respuestas del API. */
const withFacturaNotasTrimmed = (data: FacturaVenta): FacturaVenta => ({
    ...data,
    detalles: (data.detalles || []).map((d) => ({
        ...d,
        notas: normalizeItemNotas(d.notas),
    })),
});

/** Variantes de nombre de API para nro formulario / ítem master en líneas de detalle */
export const resolveMasterNumber = (detalle: any, keys: string[]): number | undefined => {
    for (const key of keys) {
        const value = detalle?.[key];
        if (value === undefined || value === null || value === '') {
            continue;
        }
        const parsed = Number(value);
        if (Number.isFinite(parsed) && parsed > 0) {
            return parsed;
        }
    }
    return undefined;
};

const facturaVentaService = {
    getFacturasVenta: async (params: FacturaVentaSearchParams): Promise<FacturaVentaPagedResult> => {
        const queryParams: any = {
            PageNumber: params.pageNumber,
            PageSize: params.pageSize,
            SearchTerm: params.searchTerm || undefined
        };

        if (params.fechaDesde) {
            queryParams.FechaDesde = params.fechaDesde.toISOString().split('T')[0];
        }
        if (params.fechaHasta) {
            queryParams.FechaHasta = params.fechaHasta.toISOString().split('T')[0];
        }
        if (params.nroCliente) {
            queryParams.NroCliente = params.nroCliente;
        }
        if (params.nroSuc) {
            queryParams.NroSuc = params.nroSuc;
        }
        if (params.nombreCliente) {
            queryParams.NombreCliente = params.nombreCliente;
        }
        if (params.letra != null && params.letra !== '') {
            queryParams.Letra = params.letra.trim();
        }
        if (params.nroComprobante != null && params.nroComprobante > 0) {
            queryParams.NroComprobante = params.nroComprobante;
        }
        if (params.estadoFactura != null && params.estadoFactura !== '') {
            queryParams.EstadoFactura = params.estadoFactura;
        }

        const response = await api.get<FacturaVentaPagedResult>('/api/FacturasVenta', {
            params: queryParams
        });
        return response.data;
    },

    getFacturaVenta: async (nroFormulario: number): Promise<FacturaVenta> => {
        const response = await api.get<FacturaVenta>(`/api/FacturasVenta/${nroFormulario}`);
        return withFacturaNotasTrimmed(response.data);
    },

    /** Solo SUPERADMIN: borra asientos vinculados al formulario y crea uno nuevo con la lógica actual. */
    regenerarAsientoFacturaVenta: async (
        id: number
    ): Promise<{ nroAsiento: number; message?: string }> => {
        const response = await api.post<{ nroAsiento: number; message?: string }>(
            `/api/FacturasVenta/${id}/regenerar-asiento`
        );
        return response.data;
    },

    obtenerCaeArgentina: async (nroFormulario: number): Promise<FacturaVenta> => {
        const response = await api.post<FacturaVenta>(`/api/FacturasVenta/${nroFormulario}/obtener-cae-argentina`);
        return withFacturaNotasTrimmed(response.data);
    },

    getLibroIvaVentas: async (params: FacturaVentaSearchParams): Promise<FacturaVentaPagedResult> => {
        const queryParams: any = {
            PageNumber: params.pageNumber,
            PageSize: params.pageSize,
            SearchTerm: params.searchTerm || undefined
        };

        if (params.fechaDesde) {
            queryParams.FechaDesde = params.fechaDesde.toISOString().split('T')[0];
        }
        if (params.fechaHasta) {
            queryParams.FechaHasta = params.fechaHasta.toISOString().split('T')[0];
        }
        if (params.nroCliente) {
            queryParams.NroCliente = params.nroCliente;
        }
        if (params.nroSuc) {
            queryParams.NroSuc = params.nroSuc;
        }
        if (params.nombreCliente) {
            queryParams.NombreCliente = params.nombreCliente;
        }

        const response = await api.get<FacturaVentaPagedResult>('/api/ComprobantesVenta/libro-iva-ventas', {
            params: queryParams
        });
        return response.data;
    },

    createFacturaVenta: async (facturaVenta: FacturaVenta & { 
        nroSuc?: number; 
        ordenCompra?: string; 
        transporte?: string;
        ubicacion?: string;
        formaDePago?: string;
        fechaEmision?: Date | string;
        horaEmision?: Date | string;
        detalles?: FacturaVentaDetalle[];
        nroFormularioPedido?: number;
        nroFormularioCotizacion?: number;
    }): Promise<FacturaVenta> => {
        // Validaciones de datos obligatorios
        const errors: string[] = [];

        // Validar cliente
        if (!facturaVenta.nroCliente || facturaVenta.nroCliente <= 0) {
            errors.push('nroCliente es obligatorio y debe ser mayor a 0');
        }

        // Obtener datos del cliente antes de crear el payload
        let clienteData: any = null;
        if (facturaVenta.nroCliente && facturaVenta.nroCliente > 0) {
            try {
                clienteData = await clienteService.getCliente(facturaVenta.nroCliente);
                if (!clienteData) {
                    errors.push(`No se encontró el cliente con número ${facturaVenta.nroCliente}`);
                }
            } catch (error: any) {
                const mensajeError = error?.response?.status === 404 
                    ? `No se encontró el cliente con número ${facturaVenta.nroCliente}`
                    : `Error al obtener los datos del cliente: ${error?.message || 'Error desconocido'}`;
                errors.push(mensajeError);
            }
        }

        // Validar que haya al menos un detalle
        if (!facturaVenta.detalles || facturaVenta.detalles.length === 0) {
            errors.push('Debe haber al menos un item en la factura');
        }

        // Validar cada detalle y obtener datos de productos
        const productosData: Record<string, any> = {};
        if (facturaVenta.detalles && facturaVenta.detalles.length > 0) {
            // Obtener datos completos de cada producto
            for (const detalle of facturaVenta.detalles) {
                if (detalle.codigoArticulo) {
                    try {
                        const producto = await productoService.getProducto(detalle.codigoArticulo);
                        if (!producto) {
                            errors.push(`No se encontró el producto con código "${detalle.codigoArticulo}"`);
                        } else {
                            productosData[detalle.codigoArticulo] = producto;
                        }
                    } catch (error: any) {
                        const itemIndex = facturaVenta.detalles.findIndex(d => d.codigoArticulo === detalle.codigoArticulo) + 1;
                        const mensajeError = error?.response?.status === 404
                            ? `Item ${itemIndex}: No se encontró el producto con código "${detalle.codigoArticulo}"`
                            : `Item ${itemIndex}: Error al obtener el producto "${detalle.codigoArticulo}": ${error?.message || 'Error desconocido'}`;
                        errors.push(mensajeError);
                    }
                }
            }

            facturaVenta.detalles.forEach((detalle, index) => {
                const itemNum = index + 1;
                // Validar codigoArticulo
                if (!detalle.codigoArticulo || String(detalle.codigoArticulo).trim() === '') {
                    errors.push(`Item ${itemNum}: codigoArticulo es obligatorio`);
                }
                // Validar descripcionArticulo
                if (!detalle.descripcionArticulo || String(detalle.descripcionArticulo).trim() === '') {
                    errors.push(`Item ${itemNum}: descripcion es obligatoria`);
                }
                // Validar cantidad
                if (detalle.cantidad === undefined || detalle.cantidad === null) {
                    errors.push(`Item ${itemNum}: cantidad es obligatoria`);
                } else if (typeof detalle.cantidad !== 'number' || isNaN(detalle.cantidad)) {
                    errors.push(`Item ${itemNum}: cantidad debe ser un número válido (tipo: ${typeof detalle.cantidad}, valor: ${detalle.cantidad})`);
                } else if (detalle.cantidad <= 0) {
                    errors.push(`Item ${itemNum}: cantidad debe ser mayor a 0 (valor actual: ${detalle.cantidad})`);
                }
            });
        }

        // Validar fechas
        if (!facturaVenta.fechaEmision) {
            errors.push('La Fecha de Emisión es obligatoria. Por favor, ingrese la fecha en la sección "Información de la Factura"');
        } else {
            const fechaEmisionDate = facturaVenta.fechaEmision instanceof Date 
                ? facturaVenta.fechaEmision 
                : new Date(facturaVenta.fechaEmision);
            if (isNaN(fechaEmisionDate.getTime())) {
                errors.push('La Fecha de Emisión debe ser una fecha válida');
            }
        }

        // Validar tipos de datos numéricos
        if (facturaVenta.nroCliente !== undefined && (typeof facturaVenta.nroCliente !== 'number' || isNaN(facturaVenta.nroCliente))) {
            errors.push('nroCliente debe ser un número válido');
        }

        if ((facturaVenta as any).nroSuc !== undefined && (facturaVenta as any).nroSuc !== null) {
            if (typeof (facturaVenta as any).nroSuc !== 'number' || isNaN((facturaVenta as any).nroSuc)) {
                errors.push('nroSuc debe ser un número válido');
            }
        }

        // Si hay errores, lanzarlos con mensaje claro
        if (errors.length > 0) {
            const errorMessage = errors.length === 1 
                ? errors[0]
                : `Se encontraron ${errors.length} errores:\n${errors.map((e, i) => `${i + 1}. ${e}`).join('\n')}`;
            console.error('❌ Errores de validación:', errors);
            throw new Error(errorMessage);
        }

        // Obtener la cotización USD por si necesitamos convertir algún precio de fallback
        let cotizacionUSD: number | null = null;
        try {
            const cotizacion = await movMonedaService.getUltimaCotizacion();
            if (cotizacion && Number.isFinite(cotizacion) && cotizacion > 0) {
                cotizacionUSD = cotizacion;
            }
        } catch (error) {
            console.warn('⚠️ No se pudo obtener cotización USD para fallback:', error);
        }

        // Mapear los detalles a la estructura de items esperada por el endpoint
        const items = (facturaVenta.detalles || []).map((detalle) => {
            const productoData = productosData[detalle.codigoArticulo || ''];
            
            // Usar el precio del detalle si existe y es mayor a 0 (ya viene convertido a ARS)
            // Si no, usar el precio del producto como fallback
            let precioUnitario = detalle.precioUnitario ?? 0;
            
            // Si el precio del detalle es 0 o inválido, usar el precio del producto
            if (!precioUnitario || precioUnitario <= 0) {
                const precioProducto = productoData?.precioVenta ?? 0;
                if (precioProducto && precioProducto > 0) {
                    // Verificar si el producto es USD y necesita conversión
                    const tipoMonedaProd = productoData?.tipoMoneda ?? productoData?.tipoMonedaArticulo ?? 0;
                    if (Number(tipoMonedaProd) === 1 && cotizacionUSD && cotizacionUSD > 0) {
                        // Convertir USD a ARS
                        precioUnitario = Number((precioProducto * cotizacionUSD).toFixed(2));
                    } else {
                        // Usar precio en ARS directamente
                        precioUnitario = Number(precioProducto);
                    }
                }
            }
            
            // Tomar el porcentaje de descuento del detalle (porcDesc es el porcentaje, detalle.descuento es el monto)
            // SIEMPRE usar detalle.porcDesc si está definido (incluso si es 0, porque 0% es válido)
            // Solo usar el descuento del producto como fallback si porcDesc realmente no está definido
            let porcDesc: number;
            
            // Si porcDesc está definido en el detalle (incluso si es 0), usarlo directamente
            // No usar detalle.descuento porque es un monto, no un porcentaje
            if (detalle.porcDesc !== undefined && detalle.porcDesc !== null) {
                porcDesc = Number(detalle.porcDesc);
            } else {
                // Solo si porcDesc realmente no está definido, usar el descuento del producto como fallback
                porcDesc = productoData?.porc_Desc ?? productoData?.porcDesc ?? 0;
            }
                        
            // Como el precio ya está convertido a ARS, siempre se envía codigoMoneda: 0 y cotizacion: 0
            const codigoMoneda = 0;
            const cotizacion = 0;

            const nroFormularioMaster = resolveMasterNumber(detalle as any, [
                'nroFormularioMaster',
                'nroformulariomaster',
                'NroFormularioMaster',
                'nro_formulario_master',
            ]);
            const nroItemMaster = resolveMasterNumber(detalle as any, [
                'nroItemMaster',
                'nroitemmaster',
                'NroItemMaster',
                'nro_item_master',
            ]);

            return {
                codigoArticulo: String(detalle.codigoArticulo ?? '').trim(),
                descripcion: String(detalle.descripcionArticulo ?? productoData?.descripcion ?? '').trim(),
                cantidad: Number(detalle.cantidad ?? 0.01),
                precioUnitario: Number(precioUnitario),
                porcDesc: Number(porcDesc),
                nroFormularioMaster,
                nroItemMaster,
                um: '', // Unidad de medida
                codigoMoneda: Number(codigoMoneda), // Siempre 0 (ARS) porque el precio ya está convertido
                cotizacion: Number(cotizacion), // Siempre 0 porque el precio ya está convertido
                notas: normalizeItemNotas(detalle.notas)
            };
        });

        // Extraer nroUbicacion del campo ubicación (formato: "NOMBRE (número)")
        let nroUbicacionOrigen = 0;
        if ((facturaVenta as any).ubicacion) {
            try {
                const ubicacionStr = String((facturaVenta as any).ubicacion);
                const match = ubicacionStr.match(/\((\d+)\)/);
                if (match && match[1]) {
                    nroUbicacionOrigen = Number(match[1]);
                }
            } catch (error) {
                nroUbicacionOrigen = 0;
            }
        }

        // Obtener el valor de formaEntrega del transporte seleccionado
        let formaEntrega: string | null = null;
        const transporteSeleccionado = String((facturaVenta as any).transporte ?? '').trim();
        if (transporteSeleccionado) {
            try {
                const transporteEncontrado = await transporteService.getTransporte(transporteSeleccionado);
                
                if (transporteEncontrado) {
                    const formaEntregaRaw = transporteEncontrado.formaEntrega ?? (transporteEncontrado as any).formaEntrega;
                    
                    if (formaEntregaRaw !== null && formaEntregaRaw !== undefined) {
                        formaEntrega = String(formaEntregaRaw).trim() || null;
                    } else {
                        formaEntrega = null;
                    }
                }
            } catch (error) {
                console.error('❌ Error al obtener transporte:', error);
                formaEntrega = null;
            }
        }

        // Obtener fechaEmision y usarla para horaEmision y fechaVencim
        const fechaEmisionDate = facturaVenta.fechaEmision 
            ? (facturaVenta.fechaEmision instanceof Date ? facturaVenta.fechaEmision : new Date(facturaVenta.fechaEmision))
            : new Date();
        const fechaEmisionISO = fechaEmisionDate.toISOString();

        const getPorcentajeBonificacion = (...values: Array<number | string | null | undefined>) => {
            for (const value of values) {
                if (value === null || value === undefined) continue;
                const num = Number(value);
                if (Number.isFinite(num)) {
                    return Math.max(0, Math.min(100, num));
                }
            }
            return 0;
        };

        const porcDescValue = getPorcentajeBonificacion(
            (facturaVenta as any).porcDesc,
            clienteData?.porcDesc,
            (clienteData as any)?.porc_Desc
        );
        const porcDesc2Value = getPorcentajeBonificacion(
            (facturaVenta as any).porcDesc2,
            clienteData?.porcDesc2,
            (clienteData as any)?.porc_Desc2
        );
        const porcDesc3Value = getPorcentajeBonificacion(
            (facturaVenta as any).porcDesc3,
            clienteData?.porcDesc3,
            (clienteData as any)?.porc_Desc3
        );

        // Construir el objeto formulario según la estructura del endpoint
        const formulario: any = {
            descripcion: String(facturaVenta.descripcion ?? '').trim(),
            nroSuc: (facturaVenta as any).nroSuc ?? 0,
            fechaEmision: fechaEmisionISO,
            horaEmision: fechaEmisionISO,
            fechaVencim: fechaEmisionISO,
            nroCliente: Number(clienteData?.nroCliente ?? facturaVenta.nroCliente ?? 0),
            nroLista: String(clienteData?.nroLista ?? facturaVenta.nroLista ?? '').trim(),
            formaDePago: String((facturaVenta as any).formaDePago ?? '').trim(),
            nroUbicacionOrigen: Number(nroUbicacionOrigen),
            ordenDeCompra: String((facturaVenta as any).ordenCompra ?? '').trim(),
            codigoDireccion: '',
            codigoDireccionEnvio: '',
            direccionEnvio: '',
            formaEntrega: formaEntrega ?? '',
            porcDesc: porcDescValue,
            porcDesc2: porcDesc2Value,
            porcDesc3: porcDesc3Value,
            integrar: 0
        };

        // Incluir idCompania si viene informada en la factura
        if ((facturaVenta as any).idCompania) {
            formulario.idCompania = String((facturaVenta as any).idCompania).trim();
        }

        // Tipo de cliente (condición frente al IVA): Responsable Inscripto (I), Monotributo (M), Consumidor Final (C), etc.
        const tipoIva = String((facturaVenta as any).tipoIVA ?? facturaVenta.tipoCliente ?? clienteData?.tipoIva ?? '').trim();
        if (tipoIva) {
            formulario.tipoIva = tipoIva;
        }
        
        // Incluir nroFormularioPedido si viene informado (para marcar el pedido como facturado)
        if (facturaVenta.nroFormularioPedido !== undefined && facturaVenta.nroFormularioPedido !== null && facturaVenta.nroFormularioPedido > 0) {
            formulario.nroFormularioPedido = Number(facturaVenta.nroFormularioPedido);
        }

        // Incluir nroFormularioCotizacion si viene informado (para marcar la cotización como facturada)
        if ((facturaVenta as any).nroFormularioCotizacion !== undefined && (facturaVenta as any).nroFormularioCotizacion !== null && (facturaVenta as any).nroFormularioCotizacion > 0) {
            formulario.nroFormularioCotizacion = Number((facturaVenta as any).nroFormularioCotizacion);
        }
        
        // Solo incluir codigoCiudadEnvio si tiene un valor válido
        const codigoCiudadEnvioValue = (facturaVenta as any).codigoCiudadEnvio;
        if (codigoCiudadEnvioValue !== undefined && codigoCiudadEnvioValue !== null && codigoCiudadEnvioValue !== 0) {
            formulario.codigoCiudadEnvio = Number(codigoCiudadEnvioValue);
        }

        // Preparar el payload según la especificación del endpoint
        const payload = {
            formulario,
            items
        };

        // Usar el endpoint de facturas: /api/FacturasVenta
        const endpoint = '/api/FacturasVenta';
        
        // Log detallado para depuración
        console.log('📤 ===== INICIO PETICIÓN POST FACTURA =====');
        console.log('📤 Endpoint:', endpoint);
        console.log('📤 Base URL:', api.defaults.baseURL);
        console.log('📤 Método: POST');
        console.log('📤 Payload completo:', JSON.stringify(payload, null, 2));
        console.log('📤 URL completa será:', `${api.defaults.baseURL}${endpoint}`);
        console.log('📤 ===== FIN LOG PRE-ENVÍO =====');

        try {
            const response = await api.post<FacturaVenta>(endpoint, payload);
            
            console.log(`✅ ===== RESPUESTA EXITOSA FACTURA =====`);
            console.log('✅ Endpoint usado:', endpoint);
            console.log('✅ Status:', response.status);
            console.log('✅ Data:', response.data);
            console.log('✅ ===== FIN RESPUESTA =====');
            
            return withFacturaNotasTrimmed(response.data);
        } catch (error: any) {
            console.error('❌ ===== ERROR EN PETICIÓN POST FACTURA =====');
            console.error('❌ Endpoint:', endpoint);
            console.error('❌ Status:', error?.response?.status);
            console.error('❌ Status Text:', error?.response?.statusText);
            console.error('❌ URL que se intentó:', error?.config?.url);
            console.error('❌ URL completa:', `${error?.config?.baseURL || api.defaults.baseURL}${error?.config?.url}`);
            console.error('❌ Response Data:', error?.response?.data);
            console.error('❌ Request Data enviada:', error?.config?.data);
            console.error('❌ Headers enviados:', error?.config?.headers);
            console.error('❌ ===== FIN ERROR =====');
            
            throw error;
        }
    },

    updateFacturaVenta: async (id: number, facturaVenta: FacturaVenta & {
        nroSuc?: number;
        ordenCompra?: string;
        transporte?: string;
        ubicacion?: string;
        formaDePago?: string;
        nroUbicacionOrigen?: number;
        formaEntrega?: string;
    }): Promise<void> => {
        // Extraer nroUbicacionOrigen de ubicación (formato: "NOMBRE (número)")
        let nroUbicacionOrigen = 0;
        if ((facturaVenta as any).ubicacion) {
            try {
                const ubicacionStr = String((facturaVenta as any).ubicacion);
                const match = ubicacionStr.match(/\((\d+)\)/);
                if (match && match[1]) {
                    nroUbicacionOrigen = Number(match[1]);
                }
            } catch {
                nroUbicacionOrigen = 0;
            }
        }

        const fechaEmision = facturaVenta.fechaEmision
            ? (facturaVenta.fechaEmision instanceof Date ? facturaVenta.fechaEmision : new Date(facturaVenta.fechaEmision))
            : new Date();
        
        // Normalizar fechaEmision a medianoche (00:00:00) para evitar problemas de zona horaria
        const fechaEmisionNormalizada = new Date(fechaEmision);
        fechaEmisionNormalizada.setHours(0, 0, 0, 0);
        
        const horaEmision = facturaVenta.horaEmision
            ? (facturaVenta.horaEmision instanceof Date ? facturaVenta.horaEmision : new Date(facturaVenta.horaEmision))
            : new Date();

        const detalles = (facturaVenta.detalles || []).map((detalle, index) => {
            const precioLista = detalle.precioLista ?? detalle.precioUnitario ?? 0;
            const porcDesc = detalle.porcDesc ?? 0;
            const descuentoMonto = (precioLista * porcDesc) / 100;
            const precioUnitario = precioLista - descuentoMonto;

            const nroFormularioMaster = resolveMasterNumber(detalle as any, [
                'nroFormularioMaster',
                'nroformulariomaster',
                'NroFormularioMaster',
                'nro_formulario_master',
            ]);
            const nroItemMaster = resolveMasterNumber(detalle as any, [
                'nroItemMaster',
                'nroitemmaster',
                'NroItemMaster',
                'nro_item_master',
            ]);

            return {
                nroIdformulario: id,
                nroItem: detalle.nroItem || (index + 1),
                nroFormularioMaster,
                nroItemMaster,
                codigoArticulo: detalle.codigoArticulo || '',
                descripcionArticulo: detalle.descripcionArticulo || '',
                cantidad: detalle.cantidad ?? 0,
                precioLista: Number(precioLista),
                porcDesc: Number(porcDesc),
                precioUnitario: Number(precioUnitario),
                descuento: Number(descuentoMonto),
                subtotal: (detalle.cantidad ?? 0) * precioUnitario,
                notas: normalizeItemNotas(detalle.notas),
            };
        });

        const payload = {
            nroFormulario: id,
            fechaEmision: fechaEmisionNormalizada.toISOString(),
            horaEmision: horaEmision.toISOString(),
            nroTipoFormulario: facturaVenta.nroTipoFormulario || '1',
            tipoCliente: facturaVenta.tipoCliente || 'C',
            nroCliente: facturaVenta.nroCliente ?? 0,
            nombre: facturaVenta.nombre || '',
            nroVendedor: facturaVenta.nroVendedor ?? 0,
            nroLista: facturaVenta.nroLista || '',
            codigoMoneda: Number(facturaVenta.codigoMoneda) || 0,
            formaDePago: (facturaVenta as any).formaDePago || '',
            nroUbicacionOrigen: nroUbicacionOrigen || (facturaVenta as any).nroUbicacionOrigen || 0,
            ordenDeCompra: (facturaVenta as any).ordenCompra || '',
            formaEntrega: (facturaVenta as any).formaEntrega || facturaVenta.formaDePago || '',
            porcDesc: facturaVenta.porcDesc ?? 0,
            porcDesc2: facturaVenta.porcDesc2 ?? 0,
            porcDesc3: facturaVenta.porcDesc3 ?? 0,
            tipoIva: (facturaVenta as any).tipoIVA ?? facturaVenta.tipoIva ?? '',
            cuit: facturaVenta.cuit || '',
            descripcion: facturaVenta.descripcion || '',
            detalles,
        };

        await api.put(`/api/FacturasVenta/${id}`, payload);
    },

    anularFactura: async (id: number): Promise<void> => {
        await api.post(`/api/FacturasVenta/${id}/anular`);
    },
};

export default facturaVentaService;

